import { useRef, useState } from 'react';
import {
  ChevronLeft,
  ImagePlus,
  Loader2,
  Sparkles,
  Trash2,
  Upload,
} from 'lucide-react';
import CachedImg from '../CachedImg';
import ProductGridSkeleton from '../ProductGridSkeleton';
import { ModalConfirmar } from '../ModalConfirmar';
import VisorFotos from './VisorFotos';
import type { Moodboard, MoodboardFoto } from '../../pages/v2/moodboardsApi';

/**
 * Lo que se ve al abrir una carpeta —
 * ADR-2026-09-13-moodboards-carpetas-de-referencia.
 *
 * Es una VISTA del mismo tab, no una ruta: entrar y salir de una carpeta no
 * debería sacar al usuario de "Marca" ni recargar la pantalla. Igual que la
 * grilla, no habla con la base: recibe las fotos y avisa lo que pasó.
 */

/** Lo que acepta el input de archivos. */
const FORMATOS = 'image/*,.heic,.heif';

const contador = (n: number) =>
  n === 0 ? 'Sin fotos' : n === 1 ? '1 foto' : `${n} fotos`;

export const VistaDeCarpeta = ({
  carpeta,
  fotos,
  cargando,
  subiendo,
  onVolver,
  onAgregar,
  onEliminarFoto,
}: {
  carpeta: Moodboard;
  fotos: MoodboardFoto[];
  cargando: boolean;
  /** Cuántas fotos están subiendo ahora (0 = ninguna). */
  subiendo: number;
  onVolver: () => void;
  onAgregar: (files: File[]) => Promise<void>;
  onEliminarFoto: (foto: MoodboardFoto) => Promise<void>;
}) => {
  const input = useRef<HTMLInputElement | null>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [aBorrar, setABorrar] = useState<MoodboardFoto | null>(null);
  const [borrando, setBorrando] = useState(false);
  const [abierta, setAbierta] = useState<number | null>(null);

  /**
   * La carpeta no tiene NADA que mostrar: ni fotos ya cargadas, ni fotos entrando, ni
   * una carga en curso que pueda traerlas. Es la condición del estado vacío, y la usan
   * las dos cosas que lo componen —la nota de ayuda y el recuadro de "arrastrá acá"—
   * para que no puedan aparecer una sin la otra.
   */
  const vacia = !cargando && fotos.length === 0 && subiendo === 0;

  const elegirArchivos = (lista: FileList | null) => {
    const files = Array.from(lista ?? []).filter((f) =>
      // El input ya filtra, pero al soltar un archivo arrastrado no filtra nada:
      // sin esto un PDF entraba al bucket y quedaba como un tile roto.
      f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name),
    );
    if (files.length) onAgregar(files);
  };

  return (
    <div>
      {/* Encabezado de la carpeta */}
      <div className="mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={onVolver}
          aria-label="Volver a las carpetas"
          className="-ml-1.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[rgb(var(--brand-black-rgb)_/_0.7)] transition-colors hover:bg-black/[0.04]"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold text-[rgb(var(--brand-black-rgb)_/_0.9)]">
            {carpeta.nombre}
          </p>
          <p className="text-[12.5px] text-[rgb(var(--brand-black-rgb)_/_0.4)]">
            {contador(fotos.length)}
            {subiendo > 0 && ` · subiendo ${subiendo}…`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-brand-primary px-5 py-2.5 text-[13.5px] font-medium text-white transition-opacity hover:opacity-90 active:opacity-80"
        >
          <ImagePlus className="h-4 w-4" />
          Agregar fotos
        </button>
      </div>

      {/* Para qué sirve la carpeta. Va ACÁ ADENTRO y no arriba de la grilla: lo que
          hay que explicar es qué cargar y para qué, y eso recién importa cuando el
          usuario ya entró a cargar algo. Contenedor con fondo y SIN borde: es una nota
          de ayuda, no un aviso ni un control — un recuadro la haría pesar de más y
          competir con las fotos, que son lo que manda en esta pantalla.

          Sólo con la carpeta VACÍA, y con la MISMA condición que el recuadro de
          "arrastrá tus referencias": las dos son el estado vacío, así que aparecen y se
          van juntas. Una vez que hay fotos, la instrucción ya se cumplió y quedaría
          ocupando el lugar de lo que el usuario vino a mirar. Se calla también mientras
          carga —si no, parpadea antes del esqueleto— y en cuanto entra la primera
          subida. */}
      {vacia && (
        <div className="mb-4 flex items-center gap-2.5 rounded-xl bg-[#F4F6FB] px-4 py-3">
          <Sparkles
            className="h-4 w-4 shrink-0 text-brand-primary"
            strokeWidth={1.75}
          />
          <p className="text-[13px] leading-relaxed text-[rgb(var(--brand-black-rgb)_/_0.7)]">
            Subí acá las imágenes que te sirvan de{' '}
            <span className="font-medium text-[rgb(var(--brand-black-rgb)_/_0.9)]">referencia visual</span> para
            una sesión de fotos: poses, luz, encuadres, paletas, locaciones. Después
            pedile al Asistente que use esta carpeta y te arma esa sesión con tus productos.
          </p>
        </div>
      )}

      <input
        ref={input}
        type="file"
        accept={FORMATOS}
        multiple
        className="hidden"
        onChange={(e) => {
          elegirArchivos(e.target.files);
          // Se limpia para que elegir DOS VECES el mismo archivo vuelva a
          // disparar `change` (si no, el segundo intento no hace nada).
          e.target.value = '';
        }}
      />

      {/* Toda el área es zona de drop. `dragleave` sólo cuenta cuando sale del
          contenedor y no de un hijo: pasar el mouse por encima de un tile
          dispara `dragleave` del tile y apagaba el resaltado a cada rato. */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setArrastrando(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            setArrastrando(false);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          setArrastrando(false);
          elegirArchivos(e.dataTransfer.files);
        }}
        className={`rounded-2xl transition-colors ${
          arrastrando ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.085)] ring-2 ring-[rgb(var(--brand-primary-rgb)_/_0.4)]' : ''
        }`}
      >
        {cargando ? (
          <ProductGridSkeleton
            count={8}
            gridClassName="grid grid-cols-3 gap-2 sm:grid-cols-4"
            tileClassName="rounded-lg"
          />
        ) : vacia ? (
          <button
            type="button"
            onClick={() => input.current?.click()}
            className="flex w-full flex-col items-center justify-center rounded-2xl border border-dashed border-black/15 bg-white py-16 text-center transition-colors hover:border-[rgb(var(--brand-primary-rgb)_/_0.5)] hover:bg-[rgb(var(--brand-primary-rgb)_/_0.085)]"
          >
            <Upload className="mb-3 h-7 w-7 text-[rgb(var(--brand-black-rgb)_/_0.3)]" strokeWidth={1.5} />
            <p className="text-[14px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.8)]">
              Arrastrá tus referencias acá
            </p>
            <p className="mt-1 text-[12.5px] text-[rgb(var(--brand-black-rgb)_/_0.4)]">
              O tocá para elegirlas de tu dispositivo
            </p>
          </button>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {/* Las que están subiendo, adelante: la foto aparece en el lugar
                donde va a quedar en cuanto termine. */}
            {Array.from({ length: subiendo }, (_, i) => (
              <div
                key={`subiendo-${i}`}
                className="flex aspect-square items-center justify-center rounded-lg bg-neutral-100"
              >
                <Loader2 className="h-4 w-4 animate-spin text-[rgb(var(--brand-black-rgb)_/_0.3)]" />
              </div>
            ))}

            {fotos.map((foto, i) => (
              <div
                key={foto.id}
                className="group relative aspect-square overflow-hidden rounded-lg bg-neutral-100"
              >
                {foto.thumbUrl ? (
                  <CachedImg
                    src={foto.thumbUrl}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-[rgb(var(--brand-black-rgb)_/_0.25)]">
                    <ImagePlus className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setAbierta(i)}
                  aria-label="Ver foto"
                  className="absolute inset-0 z-[1] cursor-zoom-in"
                />
                <button
                  type="button"
                  onClick={() => setABorrar(foto)}
                  aria-label="Eliminar foto"
                  className="absolute right-1 top-1 z-[2] inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-[rgb(var(--brand-black-rgb)_/_0.7)] opacity-100 shadow-sm backdrop-blur transition-all hover:text-red-600 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {abierta !== null && (
        <VisorFotos
          fotos={fotos}
          indice={abierta}
          onCerrar={() => setAbierta(null)}
          onCambiar={setAbierta}
        />
      )}

      <ModalConfirmar
        abierto={aBorrar !== null}
        titulo="Eliminar la foto"
        detalle="Se saca de esta carpeta. No se puede deshacer."
        confirmar="Eliminar foto"
        trabajando={borrando}
        onCancelar={() => setABorrar(null)}
        onConfirmar={async () => {
          if (!aBorrar) return;
          setBorrando(true);
          try {
            await onEliminarFoto(aBorrar);
            setABorrar(null);
          } finally {
            setBorrando(false);
          }
        }}
      />
    </div>
  );
};

export default VistaDeCarpeta;
